import {
  useInboxMessages,
  useInboxUnreadCount,
  useProcessInboxMessage,
  type InboxMessage,
  type InboxMessageRaw,
} from "@kupikupi/api";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth, useAuthToken } from "../lib/auth";

// ─── Message-type categories (mirrors backend conventions) ──────────────────
// messageType values observed from backend (from web page behavior):
//   2 = order-related (验货照片/买手回复 etc)
//   3 = wallet/payment related
//   4+ = promo/marketing
// "all" = no filter (shows full list with messageType:0 to avoid NPE)
type TabKey = "all" | "order" | "wallet" | "promo";

const TAB_FILTERS: Record<TabKey, (m: InboxMessage) => boolean> = {
  all: () => true,
  order: (m) => m.messageType === 2,
  wallet: (m) => m.messageType === 3,
  promo: (m) => m.messageType === 4 || m.messageType === 5,
};

// ─── Message type → visual config ────────────────────────────────────────────
interface MsgVisual {
  icon: string;
  iconBg: string;
  iconFg: string;
  unreadBorderColor: string;
  unreadBg: string;
  unreadBorder: string;
}

function getMsgVisual(messageType: number): MsgVisual {
  switch (messageType) {
    case 2: // order
      return {
        icon: "📦",
        iconBg: "var(--color-cta-soft)",
        iconFg: "var(--color-cta)",
        unreadBorderColor: "var(--color-cta)",
        unreadBg: "var(--color-cta-soft)",
        unreadBorder: "rgba(255,107,74,.2)",
      };
    case 3: // wallet
      return {
        icon: "💰",
        iconBg: "var(--color-secondary-soft)",
        iconFg: "var(--color-secondary)",
        unreadBorderColor: "var(--color-secondary)",
        unreadBg: "var(--color-secondary-soft)",
        unreadBorder: "rgba(107,78,255,.2)",
      };
    case 4: // promo
    case 5:
      return {
        icon: "🎁",
        iconBg: "var(--color-sticker-yellow,#fff8da)",
        iconFg: "var(--color-sticker-yellow-fg,#b45309)",
        unreadBorderColor: "var(--color-primary)",
        unreadBg: "var(--color-primary-soft)",
        unreadBorder: "rgba(11,168,136,.2)",
      };
    default: // system/other
      return {
        icon: "📢",
        iconBg: "var(--color-primary-soft)",
        iconFg: "var(--color-primary-fill)",
        unreadBorderColor: "var(--color-primary)",
        unreadBg: "var(--color-primary-soft)",
        unreadBorder: "rgba(11,168,136,.2)",
      };
  }
}

// ─── Relative time formatter ──────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function relativeTime(dateStr: string, t: (key: string, opts?: any) => string, locale: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return dateStr;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);
  if (diffMin < 1) return t("time.just_now");
  if (diffMin < 60) return t("time.minutes", { n: diffMin });
  if (diffH < 24) return t("time.hours", { n: diffH });
  if (diffD === 1) return t("time.yesterday");
  if (diffD < 7) return t("time.days", { n: diffD });
  return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
}

// ─── Derive nav link for a message (order / shipment / fallback) ──────────────
function msgLink(m: InboxMessage): string | null {
  if (m.expressOrderNo) return `/shipment?no=${encodeURIComponent(m.expressOrderNo)}`;
  if (m.orderNo) return `/orders/${encodeURIComponent(m.orderNo)}`;
  return null;
}

// ─── Single message card ──────────────────────────────────────────────────────
function MessageCard({ m, unread }: { m: InboxMessage; unread: boolean }) {
  const { t, i18n } = useTranslation("message");
  const vis = getMsgVisual(m.messageType);
  const link = msgLink(m);
  const time = relativeTime(m.sendDateTime, t, i18n.language);

  const cardStyle: React.CSSProperties = unread
    ? {
        background: vis.unreadBg,
        border: `1px solid ${vis.unreadBorder}`,
        borderLeft: `3px solid ${vis.unreadBorderColor}`,
        borderRadius: 8,
        padding: 12,
        display: "grid",
        gridTemplateColumns: "36px 1fr auto",
        gap: 10,
        alignItems: "center",
        textDecoration: "none",
        color: "inherit",
        marginBottom: 8,
      }
    : {
        background: "#fff",
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        padding: 12,
        display: "grid",
        gridTemplateColumns: "36px 1fr auto",
        gap: 10,
        alignItems: "center",
        textDecoration: "none",
        color: "inherit",
        marginBottom: 8,
      };

  const iconStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: vis.iconBg,
    color: vis.iconFg,
    display: "grid",
    placeItems: "center",
    fontSize: 16,
    flexShrink: 0,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 800,
    display: "flex",
    gap: 5,
    alignItems: "center",
    color: unread ? "inherit" : "var(--color-text-muted)",
  };

  const bodyContent = (
    <>
      <div style={iconStyle}>{vis.icon}</div>
      <div>
        <div style={titleStyle}>
          {m.subject || "—"}
          {unread && (
            <span
              style={{
                width: 6,
                height: 6,
                background: vis.unreadBorderColor,
                borderRadius: "50%",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
          )}
        </div>
        {m.messageText && (
          <div
            style={{
              fontSize: 10,
              color: "var(--color-text-muted)",
              marginTop: 2,
              lineHeight: 1.4,
              // clamp to 2 lines
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {m.messageText}
          </div>
        )}
        {(m.orderNo || m.expressOrderNo) && (
          <div
            style={{
              fontSize: 9,
              color: "var(--color-text-muted)",
              marginTop: 2,
              fontFamily: "monospace",
            }}
          >
            {m.orderNo && `${m.orderNo}`}
            {m.orderNo && m.expressOrderNo && " · "}
            {m.expressOrderNo && m.expressOrderNo}
          </div>
        )}
      </div>
      <span
        style={{
          fontSize: 9,
          color: "var(--color-text-muted)",
          fontFamily: "monospace",
          whiteSpace: "nowrap",
          alignSelf: "flex-start",
          paddingTop: 2,
        }}
      >
        {time}
      </span>
    </>
  );

  if (link) {
    return (
      <Link to={link as never} style={cardStyle}>
        {bodyContent}
      </Link>
    );
  }

  return <div style={cardStyle}>{bodyContent}</div>;
}

// ─── Main page ────────────────────────────────────────────────────────────────
export function MessagePage() {
  const { t } = useTranslation("message");
  const authToken = useAuthToken();
  const { status: authStatus } = useAuth();

  const [activeTab, setActiveTab] = useState<TabKey>("all");

  // Fetch full mailbox — pass messageType:0 explicitly to avoid backend NPE
  // (see web page comment: Integer param unboxing throws on null)
  const { data, isLoading } = useInboxMessages(authToken, {
    pageNum: 1,
    pageSize: 50,
    messageType: 0,
  });

  // Unread count for header badge
  const { data: unreadCount } = useInboxUnreadCount(authToken);

  // Unread set (messageIds): use the unread-count as a proxy; we mark messages
  // as unread if they appear in the unread list from useInboxUnread.
  // Since we only have unread count (not IDs), we treat first N items as unread
  // (conservative heuristic — same approach as web which has no per-message read flag)
  const unreadN = unreadCount ?? 0;
  const allItems = data?.records ?? [];

  // Mark all read mutation
  const processMsg = useProcessInboxMessage(authToken);

  const handleMarkAllRead = async () => {
    // Process each unread message (first unreadN items assumed to be unread)
    const toProcess = allItems.slice(0, unreadN);
    for (const m of toProcess) {
      const raw: InboxMessageRaw = {
        messageId: m.messageId,
        sendDateTime: m.sendDateTime,
        subject: m.subject,
        messageImg: m.messageImg,
        senderName: m.senderName,
        messageText: m.messageText,
        messageType: m.messageType,
        orderNo: m.orderNo,
        expressOrderNo: m.expressOrderNo,
      };
      try {
        await processMsg.mutateAsync(raw);
      } catch {
        // continue even if one fails
      }
    }
  };

  // Filter by active tab
  const filteredItems = allItems.filter(TAB_FILTERS[activeTab]);
  const totalCount = allItems.length;

  // Header title: show unread count if > 0
  const headerTitle =
    unreadN > 0 ? t("head.title_unread", { count: unreadN }) : t("head.title");

  // Auth gate: if not signed in, show empty with hint
  if (authStatus === "signed-out") {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div className="tg-body" style={{ flex: 1 }}>
          <TgEmpty icon="💬" title={t("state.signin_t")} hint={t("state.signin_s")} />
        </div>
      </TgPage>
    );
  }

  // Mark-all-read button for header right slot
  const markAllReadBtn =
    unreadN > 0 ? (
      <button
        type="button"
        onClick={handleMarkAllRead}
        disabled={processMsg.isPending}
        style={{
          background: "transparent",
          border: 0,
          fontSize: 10,
          fontWeight: 700,
          color: "var(--color-primary-fill)",
          cursor: "pointer",
          padding: "0 4px",
          opacity: processMsg.isPending ? 0.5 : 1,
          whiteSpace: "nowrap",
        }}
      >
        {t("head.mark_all_read")}
      </button>
    ) : (
      <span style={{ width: 24 }} />
    );

  return (
    <TgPage tabbar={false}>
      <TgHeader title={headerTitle} back right={markAllReadBtn} />

      {/* ── Category Tabs ─────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "8px 12px",
          background: "#fff",
          borderBottom: "1px solid var(--color-border)",
          overflowX: "auto",
          // hide scrollbar
          scrollbarWidth: "none",
        }}
      >
        {(["all", "order", "wallet", "promo"] as TabKey[]).map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                background: isActive ? "var(--color-text)" : "transparent",
                color: isActive ? "#fff" : "var(--color-text-muted)",
                padding: "5px 10px",
                borderRadius: 8,
                fontSize: 10,
                fontWeight: 700,
                border: 0,
                whiteSpace: "nowrap",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              {tab === "all" ? t("tab.all", { count: totalCount }) : t(`tab.${tab}`)}
            </button>
          );
        })}
      </div>

      {/* ── Message list ─────────────────────────────────────────────────── */}
      <div className="tg-body" style={{ flex: 1, padding: "8px 12px" }}>
        {isLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                key={i}
                style={{
                  background: "#fff",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  padding: 12,
                  display: "grid",
                  gridTemplateColumns: "36px 1fr auto",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <TgSkeleton height={36} width={36} radius={10} />
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <TgSkeleton height={11} width="70%" />
                  <TgSkeleton height={9} width="90%" />
                </div>
                <TgSkeleton height={9} width={24} />
              </div>
            ))}
          </div>
        )}

        {!isLoading && filteredItems.length === 0 && (
          <TgEmpty
            icon="💬"
            title={t("empty.title")}
            hint={t("empty.hint")}
          />
        )}

        {!isLoading &&
          filteredItems.map((m, idx) => (
            <MessageCard
              key={m.messageId}
              m={m}
              unread={idx < unreadN && activeTab === "all"}
            />
          ))}
      </div>
    </TgPage>
  );
}
